import * as React from "react";

import { Link } from "gatsby";
import Logo from "../../svgs/logo.svg";
import LogoMobile from "../../svgs/logo-mark-black.svg";
import styled from "@emotion/styled";
import { breakpoints } from "../../styles/breakpoints";
import Dropdown from "../dropdown";
import { useEffect } from "react";
import { PricingLink } from "../pricing-link";
import { Announcement } from "./announcement";

const StyledNav = styled.nav`
  position: fixed;
  width: 100%;
  z-index: 10;

  .logo {
    height: 43px;
    width: 150px;

    @media (max-width: ${breakpoints.sm}) {
      display: none;
    }
  }

  .logo-mobile {
    width: 50px;
    display: none;

    @media (max-width: ${breakpoints.sm}) {
      display: block;
    }
  }

  .container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: var(--header-height);

    @media(max-width: ${breakpoints.lg}) {
      height: var(--header-height-tablet);
    }

    @media (max-width: ${breakpoints.sm}) {
      height: var(--header-height-mobile);
    }
  }

  .mobile-btn {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    display: none;
    text-transform: uppercase;

    @media (max-width: ${breakpoints.sm}) {
      display: block;
    }
  }

  .toggle {
    display: none;
    height: 48px;
    width: 36px;
    position: relative;

    @media (max-width: ${breakpoints.xs}) {
      width: 32px;
    }

    span {
      &,
      &::before,
      &::after {
        position: absolute;
        height: 3px;
        left: 0;
        width: 100%;
        background: var(--black);
        transition: all 0.2s;
      }

      &::before,
      &::after {
        content: "";
      }

      &::before {
        top: -8px;
      }

      &::after {
        top: 8px;
      }

      &.open {
        background: transparent;

        &::before {
          top: 0;
          transform: rotate(135deg);
        }

        &::after {
          top: 0;
          transform: rotate(-135deg);
        }
      }
    }

    @media (max-width: ${breakpoints.sm}) {
      display: flex;
      align-items: center;
    }
  }

  .links {
    display: flex;
    align-items: center;
    
    & > * {
      text-transform: uppercase;
      &:not(:last-child) {
        margin-right: var(--x-small);
      }
    }

    @media (max-width: ${breakpoints.sm}) {
      display: none;
    }
  }

  .mobile-menu {
    display: none;
    position: absolute;
    height: calc(100vh - var(--header-height-mobile));
    top: var(--header-height-mobile);
    overflow-y: auto;
    z-index: 100;
    width: 100vw;
    padding: var(--medium) 0 var(--small);
    background: var(--white);
    text-transform: uppercase;
    font-size: var(--p-normal);
    text-align: left;

    @media (max-width: ${breakpoints.sm}) {
      display: block;
      flex-direction: column;
    }

    .row {
      display: flex;
      flex-direction: column;
      & > * {
        &:not(:last-child) {
          border-bottom: 1px solid #dbdbdb;
          margin-bottom: var(--macro);
        }
      }
    }

    a {
      padding: var(--micro) 0;
    }
  }
`;

export interface NavProps {
  isNavWhite?: boolean;
  navCta?: {
    text: string;
    to: string;
  };
}

export const Nav = ({ isNavWhite, navCta }: NavProps) => {
  const [mobileMenuShown, setMobileMenuShown] = React.useState(false);

  const toggle = () => {
    setMobileMenuShown(!mobileMenuShown);
  };

  const handleResize = () => {
    if (window.innerWidth > 768) {
      setMobileMenuShown(false);
    }
  };

  useEffect(() => {
    document.documentElement.style.overflowY = mobileMenuShown
      ? "hidden"
      : "auto";
    if(mobileMenuShown) {
      document.body.classList.add('mobile-menu-is-shown')
    } else {
      document.body.classList.remove('mobile-menu-is-shown')
    }

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [mobileMenuShown]);

  return (
    <StyledNav
      style={{ background: isNavWhite ? "var(--white)" : "var(--off-white)" }}
    >
      <Announcement />
      <div className="row">
        <div className="container">
          <Link to="/">
            <img src={Logo} alt="Agency Elevation" className="logo" />
            <img
              src={LogoMobile}
              alt="Agency Elevation"
              className="logo-mobile"
            />
          </Link>
          {!mobileMenuShown ? (
            <PricingLink
              to={navCta ? navCta.to : "/pricing"}
              className="btn-ghost mobile-btn"
            >
              {navCta ? navCta.text : "See Pricing"}
            </PricingLink>
          ) : null}
          <button
            aria-label="Navigation Toggle"
            className="toggle"
            onClick={toggle}
          >
            <span className={mobileMenuShown ? "open" : ""}></span>
          </button>
          <div className="links">
            <Dropdown
              text="Services"
              links={[
                { text: "White Label SEO", to: "/white-label-seo-services" },
                { text: "White Label PPC", to: "/white-label-ppc-management" },
                {
                  text: "White Label Facebook Ads",
                  to: "/white-label-facebook-ads",
                },
                {
                  text: "White Label TikTok Ads",
                  to: "/white-label-tiktok-ads",
                },
              ]}
            />
            <Link to="/results">Results</Link>
            <Link to="/about">Team</Link>
            <PricingLink
              to={navCta ? navCta.to : "/pricing"}
              className="btn-ghost"
            >
              {navCta ? navCta.text : "See Plans & Pricing"}
            </PricingLink>
          </div>
        </div>
      </div>
      {mobileMenuShown ? (
        <div
          className="mobile-menu"
          style={{
            background: isNavWhite ? "var(--white)" : "var(--off-white)",
          }}
        >
          <div className="row">
            <Link to="/white-label-seo-services">White Label SEO</Link>
            <Link to="/white-label-ppc-management">White Label PPC</Link>
            <Link to="/white-label-facebook-ads">White Label Facebook Ads</Link>
            <Link to="/white-label-tiktok-ads">White Label TikTok Ads</Link>
            <Link to="/results">Results</Link>
            <Link to="/about">Team</Link>
            <PricingLink
              to={navCta ? navCta.to : "/pricing"}
              className="btn-ghost"
            >
              {navCta ? navCta.text : "See Plans & Pricing"}
            </PricingLink>
          </div>
        </div>
      ) : null}
    </StyledNav>
  );
};
